/**
 * Create Job Dialog Component
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Phone, Mail, Check, ChevronsUpDown, FileText, Loader2, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useCreateInstallationJob, useTemplates } from '../hooks/useInstallationJobs';
import { useQuotes } from '../../operations-projects/hooks/useProjects';
import { useQuoteSensors } from '../../quotes/hooks';
import type { Quote } from '../../operations-projects/types';
import type { CreateJobInput, JobStatus } from '../types';

interface CreateJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateJobDialog({ open, onOpenChange }: CreateJobDialogProps) {
  const navigate = useNavigate();
  const createJob = useCreateInstallationJob();
  const { data: templatesData } = useTemplates();
  const [dialogContainer, setDialogContainer] = useState<HTMLDivElement | null>(null);

  // Quote selection state
  const [quoteSearch, setQuoteSearch] = useState('');
  const [quotePopoverOpen, setQuotePopoverOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  // Fetch quotes with search
  const { data: quotesData, isLoading: quotesLoading } = useQuotes(
    {
      search: quoteSearch || undefined,
      pageLength: 50,
      sortKey: 'createdAt',
      sortDirection: -1,
    },
    { enabled: open }
  );
  const quotes = useMemo(
    () => (quotesData?.quotes || []).filter((q) => !q.account_number?.startsWith('A-')),
    [quotesData]
  );

  // Fetch sensors from the selected quote
  const { sensors: quoteSensors, isLoading: isLoadingQuoteSensors } = useQuoteSensors(selectedQuote?.quote_number);

  const [formData, setFormData] = useState<CreateJobInput>({
    title: '',
    description: '',
    status: 'draft',
  });

  // Handle quote selection - auto-populate form fields
  const handleQuoteSelect = (quote: Quote) => {
    setSelectedQuote(quote);
    setQuotePopoverOpen(false);

    setFormData((prev) => ({
      ...prev,
      accountId: quote.account_id,
      mongoQuoteId: quote.mongo_quote_id || undefined,
      contactName: quote.contractual_manager_name || prev.contactName,
      contactEmail: quote.contractual_manager_email || prev.contactEmail,
      contactPhone: quote.contractual_manager_phone || prev.contactPhone,
      address: quote.shipping_address ?? '',
      title: quote.project_name || prev.title,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const job = await createJob.mutateAsync(formData);
      toast.success(`Job ${job.jobNumber} created successfully`);
      onOpenChange(false);
      resetForm();
      navigate(`/operations/installations/${job.id}`);
    } catch {
      toast.error('Failed to create job');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', status: 'draft' });
    setSelectedQuote(null);
    setQuoteSearch('');
  };

  const handleTemplateChange = (templateId: string) => {
    if (templateId === 'none') {
      setFormData((prev) => ({ ...prev, templateId: undefined, checklist: undefined }));
      return;
    }

    const template = templatesData?.templates.find((t) => t.id === templateId);
    if (template) {
      const checklist = template.checklistItems.map((item) => ({
        id: crypto.randomUUID(),
        key: item.key,
        displayName: item.displayName,
        type: item.type,
        options: item.options,
        required: item.required,
        sortOrder: item.sortOrder,
      }));
      setFormData((prev) => ({ ...prev, templateId, checklist }));
    }
  };

  const formatQuoteDisplay = (quote: Quote) => {
    const parts = [quote.quote_number];
    if (quote.account_name) parts.push(quote.account_name);
    return parts.join(' - ');
  };

  const formatQuoteSubtext = (quote: Quote) => {
    const parts = [];
    if (quote.project_name) parts.push(quote.project_name);
    if (quote.total) parts.push(`$${quote.total.toLocaleString()} ${quote.currency}`);
    return parts.join(' | ');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent
        ref={setDialogContainer}
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(event) => {
          // Escape dismisses the nested picker before the job form, including across Radix versions.
          if (quotePopoverOpen) {
            event.preventDefault();
            setQuotePopoverOpen(false);
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Installation Job</DialogTitle>
            <DialogDescription>
              Create a new installation job for technicians. Select a quote to auto-populate details.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Quote Selection */}
            <div className="grid gap-2">
              <Label>Select Quote</Label>
              {/* Keep the picker inside the dialog's focus, pointer and scroll boundary. */}
              <Popover open={quotePopoverOpen} onOpenChange={setQuotePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={quotePopoverOpen}
                    className="w-full justify-between"
                  >
                    {selectedQuote ? (
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        {formatQuoteDisplay(selectedQuote)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Search for a quote...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  container={dialogContainer}
                  className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0"
                  align="start"
                  collisionPadding={16}
                  aria-label="Select quote"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by quote number, project name, or account..."
                      value={quoteSearch}
                      onValueChange={setQuoteSearch}
                    />
                    <CommandList className="max-h-[min(300px,calc(var(--radix-popover-content-available-height)-3rem))] overscroll-contain">
                      {quotesLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span className="text-sm text-muted-foreground">Loading quotes...</span>
                        </div>
                      ) : quotes.length === 0 ? (
                        <CommandEmpty>No quotes found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {quotes.map((quote) => (
                            <CommandItem
                              key={quote.id}
                              value={quote.id}
                              onSelect={() => handleQuoteSelect(quote)}
                              className="flex flex-col items-start py-3"
                            >
                              <div className="flex items-center w-full">
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    selectedQuote?.id === quote.id ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium">{formatQuoteDisplay(quote)}</div>
                                  <div className="text-sm text-muted-foreground truncate">
                                    {formatQuoteSubtext(quote)}
                                  </div>
                                </div>
                                <span className={cn(
                                  'text-xs px-2 py-0.5 rounded',
                                  quote.status === 'approved' && 'bg-green-100 text-green-800',
                                  quote.status === 'sent' && 'bg-blue-100 text-blue-800',
                                  quote.status === 'inprogress' && 'bg-yellow-100 text-yellow-800',
                                  quote.status === 'invoice-ready' && 'bg-purple-100 text-purple-800'
                                )}>
                                  {quote.status}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Select a quote to auto-fill job details, or enter manually below.
              </p>
            </div>

            {/* Quote info (auto-populated) */}
            {selectedQuote && (
              <Card className="bg-muted/50">
                <CardContent className="pt-4 pb-3 space-y-1">
                  {selectedQuote.account_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{selectedQuote.account_name}</span>
                      {selectedQuote.account_number && (
                        <span className="text-muted-foreground">({selectedQuote.account_number})</span>
                      )}
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground">
                    Sensors in quote: {isLoadingQuoteSensors ? '...' : quoteSensors.length}
                  </div>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Job Details */}
            <div className="grid gap-2">
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Sensor Installation - Building A"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the installation job..."
                value={formData.description || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="template">Checklist Template</Label>
                <Select
                  value={formData.templateId || 'none'}
                  onValueChange={handleTemplateChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template</SelectItem>
                    {templatesData?.templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="status">Initial Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value as JobStatus }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="scheduledDate">Scheduled Date</Label>
                <Input
                  id="scheduledDate"
                  type="date"
                  value={formData.scheduledDate || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, scheduledDate: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="scheduledTime">Time</Label>
                <div className="flex gap-2">
                  <Input
                    id="scheduledTimeStart"
                    type="time"
                    placeholder="Start"
                    value={formData.scheduledTimeStart || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, scheduledTimeStart: e.target.value }))}
                  />
                  <Input
                    id="scheduledTimeEnd"
                    type="time"
                    placeholder="End"
                    value={formData.scheduledTimeEnd || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, scheduledTimeEnd: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid gap-2">
              <Label htmlFor="installation-address">Installation Address</Label>
              <Textarea
                id="installation-address"
                value={formData.address || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Shipping address from the quote, or enter the installation address"
                rows={3}
              />
            </div>

            {/* Contact Information (can be overridden) */}
            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                On-Site Contact (Optional)
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Contact name"
                  value={formData.contactName || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contactName: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Phone"
                    value={formData.contactPhone || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, contactPhone: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={formData.contactEmail || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contactEmail: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createJob.isPending || !formData.title.trim()}>
              {createJob.isPending ? 'Creating...' : 'Create Job'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
